// FILE: components/explore/ExploreFilterSidebar.tsx
"use client";

import { useState, useMemo, useCallback } from "react";
import {
  ChevronDown, Search, X, SlidersHorizontal,
  Globe, Building2, Briefcase, BookOpen, Gem, RotateCcw,
} from "lucide-react";

/* ═══════════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════════ */

export interface CategoryNode {
  id: string;
  name: string;
  slug: string;
  type: "domain" | "organization" | "position" | "subject";
  emoji?: string;
  parent_id: string | null;
}

export interface FilterState {
  domains: string[];
  orgs: string[];
  positions: string[];
  subjects: string[];
  priceType: string;    // "all" | "free" | "paid"
  sort: string;         // "hot" | "newest" | "rating"
}

export const DEFAULT_FILTERS: FilterState = {
  domains: [], orgs: [], positions: [], subjects: [],
  priceType: "all", sort: "hot",
};

interface Props {
  categories: CategoryNode[];
  filters: FilterState;
  onChange: (f: FilterState) => void;
  totalResults?: number;
  loading?: boolean;
}

/* ═══════════════════════════════════════════════════
   HELPERS — build parent-child relationships
═══════════════════════════════════════════════════ */

function useTree(categories: CategoryNode[]) {
  return useMemo(() => {
    const all = Array.isArray(categories) ? categories : [];
    const domains   = all.filter(c => c.type === "domain");
    const orgsAll   = all.filter(c => c.type === "organization");
    const posAll    = all.filter(c => c.type === "position");
    const subjects  = all.filter(c => c.type === "subject");

    // org → parent_id = domain.id
    const orgsByDomain = new Map<string, CategoryNode[]>();
    orgsAll.forEach(o => {
      if (!o.parent_id) return;
      const arr = orgsByDomain.get(o.parent_id) ?? [];
      arr.push(o);
      orgsByDomain.set(o.parent_id, arr);
    });

    // position → parent_id = org.id
    const posByOrg = new Map<string, CategoryNode[]>();
    posAll.forEach(p => {
      if (!p.parent_id) return;
      const arr = posByOrg.get(p.parent_id) ?? [];
      arr.push(p);
      posByOrg.set(p.parent_id, arr);
    });

    // Reverse lookup: org.id → domain.id
    const orgToDomain = new Map<string, string>();
    orgsAll.forEach(o => { if (o.parent_id) orgToDomain.set(o.id, o.parent_id); });

    // Reverse lookup: position.id → org.id
    const posToOrg = new Map<string, string>();
    posAll.forEach(p => { if (p.parent_id) posToOrg.set(p.id, p.parent_id); });

    return { domains, orgsAll, posAll, subjects, orgsByDomain, posByOrg, orgToDomain, posToOrg };
  }, [categories]);
}

function toggleArr(arr: string[], id: string): string[] {
  return arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id];
}

/* ═══════════════════════════════════════════════════
   SUB-COMPONENTS
═══════════════════════════════════════════════════ */

function Accordion({ title, icon: Icon, count, defaultOpen = false, children }: {
  title: string; icon: typeof Building2; count?: number;
  defaultOpen?: boolean; children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border last:border-b-0">
      <button onClick={() => setOpen(p => !p)}
        className="w-full flex items-center gap-2 px-3.5 py-3 text-left hover:bg-muted/30 transition-colors">
        <Icon className="w-3.5 h-3.5 text-teal-600 shrink-0" />
        <span className="flex-1 text-[12px] font-bold text-foreground">{title}</span>
        {count !== undefined && count > 0 && (
          <span className="text-[9px] font-bold text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded-full border border-teal-200">{count}</span>
        )}
        <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="px-3.5 pb-3">{children}</div>}
    </div>
  );
}

function SearchInput({ value, onChange, placeholder }: {
  value: string; onChange: (v: string) => void; placeholder: string;
}) {
  return (
    <div className="relative mb-2">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-8 rounded-lg border border-border bg-[#fafbfc] pl-7 pr-7 text-[11px] outline-none focus:border-teal-500 transition-all placeholder:text-muted-foreground/50"
      />
      {value && (
        <button
          onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          type="button"
        >
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function CheckItem({ label, emoji, checked, onChange }: {
  label: string; emoji?: string; checked: boolean; onChange: () => void;
}) {
  return (
    <label onClick={onChange} className="flex items-center gap-2 py-1.5 cursor-pointer group">
      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-all ${
        checked ? "bg-teal-700 border-teal-700" : "border-[#cbd5e1] group-hover:border-teal-500"
      }`}>
        {checked && (
          <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>
      {emoji && <span className="text-sm shrink-0">{emoji}</span>}
      <span className="text-[11px] text-foreground truncate">{label}</span>
    </label>
  );
}

/* ═══════════════════════════════════════════════════
   MAIN SIDEBAR
═══════════════════════════════════════════════════ */

export default function ExploreFilterSidebar({ categories, filters, onChange, totalResults, loading }: Props) {
  const { domains, orgsAll, posAll, subjects, orgsByDomain, posByOrg, orgToDomain, posToOrg } = useTree(categories);
  const [orgSearch, setOrgSearch] = useState("");
  const [posSearch, setPosSearch] = useState("");
  const [subjectSearch, setSubjectSearch] = useState("");

  const update = useCallback((patch: Partial<FilterState>) => onChange({ ...filters, ...patch }), [filters, onChange]);

  const activeCount = filters.domains.length + filters.orgs.length + filters.positions.length
    + filters.subjects.length + (filters.priceType !== "all" ? 1 : 0);

  // ── Visible orgs = orgs whose parent domain is selected (or ALL if no domain selected) ──
  const visibleOrgs = useMemo(() => {
    if (filters.domains.length === 0) return orgsAll;
    return orgsAll.filter(o => o.parent_id && filters.domains.includes(o.parent_id));
  }, [orgsAll, filters.domains]);

  // Filter visibleOrgs theo search
  const filteredOrgs = useMemo(() => {
    if (!orgSearch.trim()) return visibleOrgs;
    const lc = orgSearch.toLowerCase();
    return visibleOrgs.filter(o => o.name.toLowerCase().includes(lc));
  }, [visibleOrgs, orgSearch]);

  // Position độc lập, không filter theo org
  const filteredPositions = useMemo(() => {
    if (!posSearch.trim()) return posAll;
    const lc = posSearch.toLowerCase();
    return posAll.filter(p => p.name.toLowerCase().includes(lc));
  }, [posAll, posSearch]);

  // ── Filtered subjects ──
  const filteredSubjects = useMemo(() => {
    if (!subjectSearch.trim()) return subjects;
    const lc = subjectSearch.toLowerCase();
    return subjects.filter(s => s.name.toLowerCase().includes(lc));
  }, [subjects, subjectSearch]);

  // ══ DOMAIN toggle — bỏ chọn domain → xóa orgs con + positions cháu ══
  const handleDomainToggle = useCallback((domainId: string) => {
    const newDomains = toggleArr(filters.domains, domainId);
    let newOrgs = [...filters.orgs];
    let newPos = [...filters.positions];

    if (!newDomains.includes(domainId)) {
      // Bỏ domain → xóa orgs con (position độc lập, không xóa)
      const childOrgIds = (orgsByDomain.get(domainId) ?? []).map(o => o.id);
      newOrgs = newOrgs.filter(id => !childOrgIds.includes(id));
    }

    onChange({ ...filters, domains: newDomains, orgs: newOrgs, positions: newPos });
  }, [filters, orgsByDomain, posByOrg, onChange]);

  // ══ ORG toggle — chọn org → tự tick domain cha; bỏ org → xóa positions con ══
  const handleOrgToggle = useCallback((orgId: string) => {
    const newOrgs = toggleArr(filters.orgs, orgId);
    let newDomains = [...filters.domains];
    let newPos = [...filters.positions];

    if (newOrgs.includes(orgId)) {
      // Chọn org → auto-tick domain cha
      const parentDomainId = orgToDomain.get(orgId);
      if (parentDomainId && !newDomains.includes(parentDomainId)) {
        newDomains = [...newDomains, parentDomainId];
      }
    }
    // Position độc lập, không xóa khi bỏ org

    onChange({ ...filters, domains: newDomains, orgs: newOrgs, positions: newPos });
  }, [filters, orgToDomain, posByOrg, onChange]);

  // ══ POSITION toggle — chọn position → tự tick org cha + domain ông ══
  const handlePositionToggle = useCallback((posId: string) => {
    const newPos = toggleArr(filters.positions, posId);
    let newOrgs = [...filters.orgs];
    let newDomains = [...filters.domains];

    // Position độc lập, không auto-tick org/domain cha

    onChange({ ...filters, domains: newDomains, orgs: newOrgs, positions: newPos });
  }, [filters, posToOrg, orgToDomain, onChange]);

  const handleReset = () => onChange({ ...DEFAULT_FILTERS });

  return (
    <div className="bg-white border border-border rounded-2xl shadow-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3.5 py-3 border-b border-border bg-[#fafbfc]">
        <SlidersHorizontal className="w-4 h-4 text-teal-700" />
        <span className="text-[13px] font-extrabold text-teal-800 flex-1">Bộ lọc</span>
        {activeCount > 0 && (
          <>
            <span className="text-[9px] font-bold text-white bg-teal-700 w-5 h-5 rounded-full flex items-center justify-center">{activeCount}</span>
            <button onClick={handleReset}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold text-red-500 hover:bg-red-50 transition-colors">
              <RotateCcw className="w-2.5 h-2.5" />Xóa lọc
            </button>
          </>
        )}
      </div>

      {/* ── 1. Lĩnh vực ── */}
      <Accordion title="Lĩnh vực" icon={Globe} count={filters.domains.length} defaultOpen>
        <div className="flex flex-col max-h-[200px] overflow-y-auto scrollbar-thin">
          {domains.map(d => (
            <CheckItem key={d.id} label={d.name} emoji={d.emoji}
              checked={filters.domains.includes(d.id)}
              onChange={() => handleDomainToggle(d.id)} />
          ))}
          {domains.length === 0 && <p className="text-[10px] text-muted-foreground/60 italic py-2">Chưa có dữ liệu</p>}
        </div>
      </Accordion>

      {/* ── 2. Tổ chức (visible theo domain đã chọn) ── */}
      <Accordion title="Tổ chức" icon={Building2} count={filters.orgs.length}>
        <div>
          <SearchInput value={orgSearch} onChange={setOrgSearch} placeholder="Tìm tổ chức..." />
          <div className="flex flex-col max-h-[240px] overflow-y-auto scrollbar-thin">
            {filteredOrgs.length > 0 ? filteredOrgs.map(o => (
              <CheckItem key={o.id} label={o.name} emoji={o.emoji}
                checked={filters.orgs.includes(o.id)}
                onChange={() => handleOrgToggle(o.id)} />
            )) : (
              <p className="text-[10px] text-muted-foreground/60 italic py-2">
                {orgSearch ? "Không tìm thấy" : (filters.domains.length > 0 ? "Không có tổ chức thuộc lĩnh vực đã chọn" : "Chưa có dữ liệu")}
              </p>
            )}
          </div>
        </div>
      </Accordion>

      {/* ── 3. Vị trí (visible theo org đã chọn) ── */}
      <Accordion title="Vị trí" icon={Briefcase} count={filters.positions.length}>
        <div>
          <SearchInput value={posSearch} onChange={setPosSearch} placeholder="Tìm vị trí..." />
          <div className="flex flex-col max-h-[240px] overflow-y-auto scrollbar-thin">
            {filteredPositions.length > 0 ? filteredPositions.map(p => (
              <CheckItem key={p.id} label={p.name} emoji={p.emoji}
                checked={filters.positions.includes(p.id)}
                onChange={() => handlePositionToggle(p.id)} />
            )) : (
              <p className="text-[10px] text-muted-foreground/60 italic py-2">
                {posSearch ? "Không tìm thấy" : "Chưa có vị trí"}
              </p>
            )}
          </div>
        </div>
      </Accordion>

      {/* ── 4. Môn học (with inner search) ── */}
      <Accordion title="Môn học" icon={BookOpen} count={filters.subjects.length}>
        <div>
          <SearchInput value={subjectSearch} onChange={setSubjectSearch} placeholder="Tìm môn học..." />
          <div className="flex flex-col max-h-[200px] overflow-y-auto scrollbar-thin">
            {filteredSubjects.map(s => (
              <CheckItem key={s.id} label={s.name} emoji={s.emoji}
                checked={filters.subjects.includes(s.id)}
                onChange={() => update({ subjects: toggleArr(filters.subjects, s.id) })} />
            ))}
            {filteredSubjects.length === 0 && (
              <p className="text-[10px] text-muted-foreground/60 italic py-2">
                {subjectSearch ? "Không tìm thấy" : "Chưa có môn học"}
              </p>
            )}
          </div>
        </div>
      </Accordion>

      {/* ── 5. Giá ── */}
      <Accordion title="Giá" icon={Gem} count={filters.priceType !== "all" ? 1 : 0}>
        <div className="flex flex-col">
          {[
            { id: "all", label: "Tất cả" },
            { id: "free", label: "Miễn phí" },
            { id: "paid", label: "Có phí" },
          ].map(p => (
            <label key={p.id} className="flex items-center gap-2 py-1.5 cursor-pointer group">
              <div className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-all ${
                filters.priceType === p.id ? "bg-teal-700 border-teal-700" : "border-[#cbd5e1] group-hover:border-teal-500"
              }`}>
                {filters.priceType === p.id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
              </div>
              <span className="text-[11px] text-foreground">{p.label}</span>
            </label>
          ))}
        </div>
      </Accordion>

      {/* Footer */}
      {totalResults !== undefined && (
        <div className="px-3.5 py-2.5 border-t border-border bg-[#fafbfc] text-center">
          <span className="text-[10px] text-muted-foreground">
            {loading ? "Đang tải..." : `${totalResults.toLocaleString()} kết quả`}
          </span>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   MOBILE BOTTOM SHEET WRAPPER
═══════════════════════════════════════════════════ */

export function MobileFilterSheet({ filters, onChange, onClose, categories }: {
  filters: FilterState; onChange: (f: FilterState) => void;
  onClose: () => void; categories: CategoryNode[];
}) {
  
  return (
    <div className="fixed inset-0 z-[70] md:hidden" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div onClick={e => e.stopPropagation()}
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto shadow-[0_-10px_40px_rgba(0,0,0,.15)] animate-slide-up">
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-10 h-1 rounded-full bg-muted-foreground/20" />
        </div>
        <ExploreFilterSidebar filters={filters} onChange={onChange} categories={categories} />
        <div className="sticky bottom-0 p-3 bg-white border-t border-border">
          <button onClick={onClose}
            className="w-full py-3 rounded-full bg-teal-800 text-white text-[13px] font-bold hover:bg-teal-700 transition-colors">
            Áp dụng bộ lọc
          </button>
        </div>
      </div>
    </div>
  );
}